/**
 * MessageCard — renders one followup message with inline edit + delete + move
 * buttons (Story 7.8).
 *
 * Uses move up / move down buttons (fallback — @dnd-kit is NOT installed in
 * the admin workspace and the task notes allow this MVP approach). When a
 * drag-drop story lands later, swap the up/down buttons for a drag handle.
 */

import { useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Trash2, X, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';

export interface MessageCardDto {
  id: string;
  ruleId: string;
  position: number;
  content: string;
}

interface Props {
  message: MessageCardDto;
  isFirst: boolean;
  isLast: boolean;
  submitting?: boolean;
  onSaveContent: (id: string, nextContent: string) => void;
  onDelete: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
}

export function MessageCard({
  message,
  isFirst,
  isLast,
  submitting,
  onSaveContent,
  onDelete,
  onMoveUp,
  onMoveDown,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);

  function handleSave() {
    if (draft.trim() === '') return;
    onSaveContent(message.id, draft);
    setEditing(false);
  }

  function handleCancel() {
    setDraft(message.content);
    setEditing(false);
  }

  return (
    <div
      data-testid={`message-card-${message.id}`}
      className="flex items-start gap-3 rounded-md border border-input bg-card p-3"
    >
      <div className="flex shrink-0 flex-col items-center gap-1">
        <span
          className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold"
          aria-label={`Posição ${message.position}`}
        >
          #{message.position}
        </span>
        <div className="flex flex-col">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0"
            disabled={isFirst || submitting}
            onClick={() => onMoveUp(message.id)}
            aria-label={`Mover mensagem ${message.position} para cima`}
          >
            <ArrowUp size={14} aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0"
            disabled={isLast || submitting}
            onClick={() => onMoveDown(message.id)}
            aria-label={`Mover mensagem ${message.position} para baixo`}
          >
            <ArrowDown size={14} aria-hidden />
          </Button>
        </div>
      </div>

      <div className="min-w-0 flex-1">
        {editing ? (
          <div className="space-y-2">
            <textarea
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={draft}
              disabled={submitting}
              onChange={(e) => setDraft(e.target.value)}
              aria-label="Editar conteúdo"
            />
            <div className="flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleSave}
                disabled={submitting || draft.trim() === ''}
                aria-label="Salvar conteúdo"
              >
                <Check size={14} aria-hidden className="mr-1" />
                Salvar
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={handleCancel}
                disabled={submitting}
                aria-label="Cancelar edição"
              >
                <X size={14} aria-hidden className="mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>
        )}
      </div>

      {!editing && (
        <div className="flex shrink-0 gap-1">
          <Button
            size="sm"
            variant="ghost"
            disabled={submitting}
            onClick={() => setEditing(true)}
            aria-label={`Editar mensagem ${message.position}`}
          >
            <Pencil size={14} aria-hidden />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={submitting}
            onClick={() => onDelete(message.id)}
            aria-label={`Deletar mensagem ${message.position}`}
          >
            <Trash2 size={14} aria-hidden />
          </Button>
        </div>
      )}
    </div>
  );
}

export default MessageCard;
